import React, { useEffect, useRef } from 'react'
import { Grid, H1, Separator, Skeleton, useContainerWidth } from '@fs/zion-ui'
import { Redirect, Route, Switch } from '@fs/zion-router'
import { useTreeGroupDetailsContext } from '../providers/TreeGroupDetailsProvider'
import SideNav, { SideNavSkeleton, TopNav, TopNavSkeleton } from './SideNav'
import { getTabUrl, type TabName, useTreeSettingsParams, useTreeSettingsRouteMatch } from '../util'
import DetailsContent, { DetailsContentSkeleton } from './DetailsContent'
import ContributorsContent, { ContributorsContentSkeleton } from './ContributorsContent'
import PrivacyContent from './PrivacyContent'
import { useMembersContext } from '../../providers/MembersProvider'
import LatestChangesContent from './LatestChangesContent'
import { useStartingPerson } from '../../providers/StartingPersonProvider'
import { useUserIsDataAdmin } from '../../providers/DataAdminProvider'

const subPageContentSkeletonMap = {
  'latest-changes': 'div',
  contributors: ContributorsContentSkeleton,
  details: DetailsContentSkeleton,
  privacy: 'div',
} as const satisfies Record<TabName, unknown>

function TabContent(): JSX.Element {
  const match = useTreeSettingsRouteMatch()
  const { userIsAdmin } = useMembersContext()

  return (
    <Switch>
      <Route exact path={getTabUrl('details', match)}>
        <DetailsContent />
      </Route>
      <Route exact path={getTabUrl('privacy', match)}>
        {userIsAdmin ? <PrivacyContent /> : <Redirect to={getTabUrl('details', match)} />}
      </Route>
      <Route exact path={getTabUrl('latest-changes', match)}>
        <LatestChangesContent />
      </Route>
      <Route exact path={getTabUrl('contributors', match)}>
        <ContributorsContent />
      </Route>
    </Switch>
  )
}

export default function SettingsContent(): JSX.Element | null {
  const { group } = useTreeGroupDetailsContext()
  const { tab } = useTreeSettingsParams()
  const { loading, startingPersonId, startingPersonOverlay } = useStartingPerson()
  const { userIsDataAdmin } = useUserIsDataAdmin()
  const { userIsAdmin } = useMembersContext()
  const hasOpenedStartingPerson = useRef(false)
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, lg: false })

  useEffect(() => {
    if (!loading && !startingPersonId && !hasOpenedStartingPerson.current && userIsAdmin && !userIsDataAdmin) {
      hasOpenedStartingPerson.current = true
      startingPersonOverlay.open()
    }
  }, [loading, startingPersonId, startingPersonOverlay, userIsAdmin, userIsDataAdmin])

  return (
    <div>
      {/* TODO translations */}
      <H1 size="H4">Tree Settings: {group.name}</H1>
      <Separator />

      {isMobile ? (
        <Grid>
          <TopNav tab={tab} />
          <Separator size="nano" />
          <TabContent />
        </Grid>
      ) : (
        <Grid>
          <SideNav columns={3} tab={tab} />
          <TabContent columns={9} />
        </Grid>
      )}
    </div>
  )
}

type SettingsContentSkeletonProps = {
  ContentSkeleton: (() => JSX.Element) | 'div'
}

export function SettingsContentSkeleton({ ContentSkeleton }: SettingsContentSkeletonProps): JSX.Element {
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, lg: false })

  return (
    <div>
      <Skeleton.H4 />
      <Separator />
      {isMobile ? (
        <>
          <TopNavSkeleton />
          <ContentSkeleton />
        </>
      ) : (
        <Grid>
          <SideNavSkeleton columns={3} />
          <ContentSkeleton columns={9} />
        </Grid>
      )}
    </div>
  )
}

export function TabSettingsContentSkeleton(): JSX.Element {
  const { tab } = useTreeSettingsParams()
  const ContentSkeleton = subPageContentSkeletonMap[tab]
  return <SettingsContentSkeleton ContentSkeleton={ContentSkeleton} />
}
